import { filterRangeToApiParam } from "./lib/filterDates.js";
import { getBrowserTimezone, offsetMinutesForTimezone } from "./lib/reportingTimezone.js";
import type {
  AcquisitionData,
  DailyUptimeRow,
  EngagementData,
  EventsData,
  EventsSortColumn,
  EventsSortDir,
  FilterOptions,
  FilterState,
  HealthData,
  HealthTelemetryMinutesQuery,
  HealthTelemetryMinutesResult,
  InteractionSessionCohort,
  MeResponse,
  PaidSessionExplorerData,
  PresenceData,
  RetentionData,
  SessionJourneyData,
  SessionJourneyPaymentConfirmed,
  SessionJourneySegment,
  SessionsData,
  UptimeTimelineSegment,
  WifiOverview,
  WifiSignalSeries,
} from "./types.js";

interface ApiSourceInfo {
  baseUrl: string;
  label: string;
}

/** When the UI is served from local machine but not from the API port (6180), JSON always goes to the analytics-api dev server. */
function localDevApiBase(): string | null {
  const { hostname, port } = window.location;
  const localHost = hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
  if (!localHost) return null;
  if (port === "6180") return null;
  return "http://localhost:6180";
}

export function getApiSourceInfo(): ApiSourceInfo {
  const config = (window as any).serverConfig;
  if (config?.API_URL) {
    return { baseUrl: config.API_URL, label: "Server Config" };
  }
  const params = new URLSearchParams(window.location.search);
  const explicitApiUrl = params.get("api_url");
  if (explicitApiUrl) {
    return { baseUrl: explicitApiUrl, label: "Custom URL" };
  }
  const env = params.get("env");
  if (env === "main") return { baseUrl: "https://analytics.main.teamtable.co", label: "Main" };
  if (env === "stg") return { baseUrl: "https://analytics.stg.teamtable.co", label: "Staging" };
  if (env === "production" || env === "prod") {
    return { baseUrl: "https://analytics.teamtable.co", label: "Production" };
  }
  const localApi = localDevApiBase();
  if (localApi) {
    return { baseUrl: localApi, label: "Local API" };
  }
  return { baseUrl: window.location.origin, label: "Current Origin" };
}

let apiReportingTimezone = getBrowserTimezone();

export function setApiReportingTimezone(timeZone: string): void {
  apiReportingTimezone = timeZone || "UTC";
}

/**
 * Thrown when the API says the caller is not signed in, or is reaching for a fleet that is not
 * theirs. Distinct from a transport error so the shell can offer sign-in rather than a stack trace.
 */
export class AuthRequiredError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(status === 403 ? "That fleet is not yours" : "Not signed in");
    this.name = "AuthRequiredError";
  }
}

async function readErrorDetail(res: Response): Promise<string> {
  try {
    const body = (await res.clone().json()) as { error?: unknown };
    if (typeof body.error === "string" && body.error.trim()) return body.error;
  } catch {
    /* non-JSON error body */
  }
  return res.statusText;
}

async function throwForAuth(res: Response): Promise<void> {
  if (res.status !== 401 && res.status !== 403) return;
  let code = "not_authenticated";
  try {
    code = ((await res.clone().json()) as { code?: string }).code ?? code;
  } catch {
    /* non-JSON body: fall back to the default code */
  }
  throw new AuthRequiredError(res.status, code);
}

/**
 * The browser reports a blocked cross-origin request as a bare `TypeError: Failed to fetch` —
 * no status, no headers, nothing to distinguish "CORS refused" from "network down". When the
 * dashboard on one origin (usually localhost + `?env=`) calls a hosted API, translate that into
 * an explanation a human can act on.
 */
async function fetchOrExplain(url: URL): Promise<Response> {
  try {
    return await fetch(url.toString(), { credentials: "include" });
  } catch (err) {
    if (!(err instanceof TypeError)) throw err;
    const crossOrigin = url.origin !== window.location.origin;
    if (!crossOrigin) {
      throw new Error(
        `Could not reach the API at ${url.origin} (Failed to fetch). ` +
          `Is the dev server running? (npm run iterate)`,
      );
    }
    throw new Error(
      `The browser blocked the request from ${window.location.origin} to ${url.origin} (Failed to fetch). ` +
        `Most likely one of: ` +
        `1) that environment's ALLOWED_ORIGINS does not list ${window.location.origin} (CORS refused), ` +
        `2) Chrome is blocking the cross-site teamtable.co session cookie — check the eye icon in the address bar, ` +
        `3) you are offline or the host is unreachable. ` +
        `Sign in at the admin portal, or try the dashboard on ${url.origin} directly.`,
    );
  }
}

function filtersToParams(filters: FilterState): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.fleetId && filters.fleetId !== "all") params.set("fleet_id", filters.fleetId);
  if (filters.venueId && filters.venueId !== "all") params.set("venue_id", filters.venueId);
  if (filters.deviceId && filters.deviceId !== "all") params.set("device_id", filters.deviceId);
  if (filters.gameUrl && filters.gameUrl !== "all") params.set("game_url", filters.gameUrl);
  if (filters.includeNonPublic) params.set("include_non_public", "1");
  if (filters.from) params.set("from", filterRangeToApiParam(filters.from));
  if (filters.to) params.set("to", filterRangeToApiParam(filters.to));
  params.set("reporting_tz", apiReportingTimezone);
  params.set("tz_offset", String(offsetMinutesForTimezone(apiReportingTimezone)));
  return params;
}

async function fetchJson<T>(path: string, filters?: FilterState): Promise<T> {
  const { baseUrl } = getApiSourceInfo();
  const url = new URL(path, baseUrl);
  if (filters) {
    const params = filtersToParams(filters);
    params.forEach((value, key) => url.searchParams.set(key, value));
  }
  const res = await fetchOrExplain(url);
  await throwForAuth(res);
  if (!res.ok) {
    throw new Error(`API error: ${res.status} ${await readErrorDetail(res)}`);
  }
  const contentType = res.headers.get("content-type") || "";
  if (!contentType.toLowerCase().includes("application/json")) {
    const sample = (await res.text()).slice(0, 80).replace(/\s+/g, " ").trim();
    throw new Error(
      `Expected JSON but got ${contentType || "unknown content-type"} from ${url.pathname}. ` +
        `This usually means auth redirect or endpoint not deployed yet. Response starts: ${sample}`,
    );
  }
  return res.json() as Promise<T>;
}

async function fetchExportText(
  path: string,
  filters: FilterState,
  extraParams?: Record<string, string>,
  opts?: { expectContentType?: "json" | "csv" },
): Promise<string> {
  const { baseUrl } = getApiSourceInfo();
  const url = new URL(path, baseUrl);
  const params = filtersToParams(filters);
  params.forEach((value, key) => url.searchParams.set(key, value));
  if (extraParams) {
    for (const [k, v] of Object.entries(extraParams)) {
      url.searchParams.set(k, v);
    }
  }
  const res = await fetchOrExplain(url);
  const raw = await res.text();
  if (!res.ok) {
    let detail = raw.slice(0, 200);
    try {
      const errBody = JSON.parse(raw) as { error?: string };
      if (typeof errBody.error === "string") detail = errBody.error;
    } catch {
      /* keep slice */
    }
    throw new Error(`Export failed (${res.status}): ${detail || res.statusText}`);
  }
  const contentType = (res.headers.get("content-type") || "").toLowerCase();
  const expect = opts?.expectContentType ?? "json";
  const okType =
    expect === "csv"
      ? contentType.includes("text/csv") || contentType.includes("application/csv")
      : contentType.includes("application/json");
  if (!okType) {
    const sample = raw.slice(0, 120).replace(/\s+/g, " ").trim();
    throw new Error(
      `Export expected ${expect.toUpperCase()} but got ${contentType || "unknown content-type"} from ${url.pathname}. ` +
        `Often the API is missing this route (SPA HTML) or the request hit the wrong host. Starts: ${sample}`,
    );
  }
  return raw;
}

function triggerBrowserDownload(text: string, filename: string, mime: string): void {
  const blob = new Blob([text], { type: mime });
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

const TEMP_BUCKET_ORDER = ["<55", "55-60", "60-65", "65-70", "70-80", "80-85", "85+"];

function mapDailyUptimeRow(row: any): DailyUptimeRow {
  const capMs = Number(row.capacityMs) || 0;
  const up = (Number(row.uptimeMs) || 0) / 3600000;
  const sched = (Number(row.scheduledMs) || 0) / 3600000;
  const unsched = (Number(row.unscheduledMs) || 0) / 3600000;
  const cap = capMs / 3600000;
  const base = {
    day: String(row.day ?? ""),
    uptimeHours: up,
    scheduledHours: sched,
    unscheduledHours: unsched,
    unmeasuredHours: (Number(row.unmeasuredMs) || 0) / 3600000,
    capacityHours: cap,
    capacityMs: capMs,
  };
  const mapSeg = (seg: unknown): UptimeTimelineSegment => {
    const o = seg as Record<string, unknown>;
    const kindRaw = o.kind;
    const kind: UptimeTimelineSegment["kind"] =
      kindRaw === "unmeasured"
        ? "unmeasured"
        : kindRaw === "scheduled"
          ? "scheduled"
          : kindRaw === "up"
            ? "up"
            : "unscheduled";
    return {
      startMsInDay: Number(o.startMsInDay) || 0,
      endMsInDay: Number(o.endMsInDay) || 0,
      kind,
      tooltip: String(o.tooltip ?? ""),
    };
  };

  return { ...base, timeline: Array.isArray(row.timeline) ? row.timeline.map(mapSeg) : [] };
}

function transformHealth(raw: any): HealthData {
  const totalEvents = Number(raw.totalEvents) || 0;

  const distMap: Record<string, number> = {};
  for (const row of raw.temperature?.distribution ?? []) {
    distMap[row.bucket] = Number(row.count) || 0;
  }

  const dailyUptimeByDevice = (raw.dailyUptimeByDevice ?? []).map((entry: any) => ({
    deviceId: String(entry.deviceId ?? ""),
    deviceName: String(entry.deviceName ?? "").trim(),
    venueId: String(entry.venueId ?? "").trim(),
    venueName: String(entry.venueName ?? "").trim(),
    dailyUptime: (entry.dailyUptime ?? []).map(mapDailyUptimeRow),
  }));

  const lookbackDailyUptimeByDevice = (raw.lookbackDailyUptimeByDevice ?? []).map((entry: any) => ({
    deviceId: String(entry.deviceId ?? ""),
    deviceName: String(entry.deviceName ?? "").trim(),
    venueId: String(entry.venueId ?? "").trim(),
    venueName: String(entry.venueName ?? "").trim(),
    dailyUptime: (entry.dailyUptime ?? []).map(mapDailyUptimeRow),
  }));

  const devicesFromPayload = (raw.devices ?? []).map((d: any) => ({
    id: String(d.id ?? ""),
    name: String(d.name ?? "").trim(),
    venueId: String(d.venueId ?? "").trim(),
    venueName: String(d.venueName ?? "").trim(),
  }));
  const devices =
    devicesFromPayload.length > 0
      ? devicesFromPayload.filter((d: { id: string; name: string }) => d.id && d.name)
      : dailyUptimeByDevice
          .filter((d: { deviceId: string; deviceName: string }) => d.deviceId && d.deviceName)
          .map(
            (d: { deviceId: string; deviceName: string; venueId: string; venueName: string }) => ({
              id: d.deviceId,
              name: d.deviceName,
              venueId: d.venueId,
              venueName: d.venueName,
            }),
          );

  return {
    eventCount: totalEvents,
    avgTemp: Number(raw.temperature?.average) || 0,
    heatCutoverIso:
      typeof raw.temperature?.cutoverIso === "string" ? raw.temperature.cutoverIso : null,
    heatSource:
      raw.temperature?.source === "device_telemetry_minutes"
        ? "device_telemetry_minutes"
        : "unavailable",
    scheduledPct: Number(raw.scheduledPct) || 0,
    unscheduledPct: Number(raw.unscheduledPct) || 0,
    unmeasuredPct: Number(raw.unmeasuredPct) || 0,
    dailyUptime: (raw.dailyUptime ?? []).map(mapDailyUptimeRow),
    lookbackDailyUptime: (raw.lookbackDailyUptime ?? []).map(mapDailyUptimeRow),
    dailyUptimeByDevice,
    lookbackDailyUptimeByDevice,
    devices,
    tableCount: Math.max(0, Number(raw.tableCount) || devices.length),
    tempBuckets: TEMP_BUCKET_ORDER.map((b) => distMap[b] ?? 0),
    topIssues: (raw.topIssues ?? []).map((r: any) => {
      const eventName = String(r.event_name ?? "");
      const issueType = String(r.issue_type ?? "");
      const scope = String(r.scope ?? "");
      const severity: "warning" | "error" = r.severity === "warning" ? "warning" : "error";
      const typeDisp = issueType.trim() || "—";
      const scopeDisp = scope.trim();
      const label = scopeDisp
        ? `${typeDisp} (${eventName}) · ${scopeDisp}`
        : `${typeDisp} (${eventName})`;
      return {
        label,
        count: Number(r.count) || 0,
        eventName,
        issueType,
        scope,
        severity,
      };
    }),
    hourlyIssueCounts: Array.from({ length: 24 }, (_, i) => {
      const row = (raw.issuesByHour ?? []).find((r: any) => Number(r.hour) === i);
      return row ? Number(row.count) || 0 : 0;
    }),
    issuesByDay: (raw.issuesByDay ?? raw.issues_by_day ?? []).map((r: any) => ({
      day: String(r.day ?? ""),
      warningCount: Number(r.warningCount ?? r.warning_count) || 0,
      errorCount: Number(r.errorCount ?? r.error_count) || 0,
      capacityHours: Number(r.capacityHours ?? r.capacity_hours) || 0,
    })),
    issuesByDeviceDay: (raw.issuesByDeviceDay ?? raw.issues_by_device_day ?? []).map((r: any) => ({
      deviceId: String(r.deviceId ?? r.device_id ?? ""),
      day: String(r.day ?? ""),
      warningCount: Number(r.warningCount ?? r.warning_count) || 0,
      errorCount: Number(r.errorCount ?? r.error_count) || 0,
    })),
  };
}

const MENU_BUCKET_ORDER = ["<15s", "15-30s", "30-60s", "1-2m", "2m+"];

const INTERACTION_SESSION_DURATION_BUCKET_ORDER = [
  "<1m",
  "1-5m",
  "5-15m",
  "15-30m",
  "30m-1h",
  "1h+",
];

const INTERACTION_SESSION_COHORT_ORDER: InteractionSessionCohort[] = [
  "<1m",
  "1-5m",
  "5-30m",
  "30m+",
];

function transformSessionJourneySegment(raw: unknown): SessionJourneySegment {
  const s = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    screenName: String(s.screenName ?? s.screen_name ?? "unknown"),
    gameName:
      typeof s.gameName === "string"
        ? s.gameName
        : typeof s.game_name === "string"
          ? s.game_name
          : null,
    gameRoute:
      typeof s.gameRoute === "string"
        ? s.gameRoute
        : typeof s.game_route === "string"
          ? s.game_route
          : null,
    label: String(s.label ?? "Unknown"),
    durationMs: Number(s.durationMs ?? s.duration_ms) || 0,
    startTs: String(s.startTs ?? s.start_ts ?? ""),
    endTs: String(s.endTs ?? s.end_ts ?? ""),
    inProgress: Boolean(s.inProgress ?? s.in_progress),
  };
}

function transformSessionJourneyPayment(raw: unknown): SessionJourneyPaymentConfirmed {
  const p = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const paidSessionIdRaw = p.paidSessionId ?? p.paid_session_id;
  return {
    at: String(p.at ?? ""),
    durationMinutes: Number(p.durationMinutes ?? p.duration_minutes) || 0,
    paidSessionId:
      typeof paidSessionIdRaw === "string" && paidSessionIdRaw.trim()
        ? paidSessionIdRaw.trim()
        : null,
  };
}

function transformSessionJourney(raw: any): SessionJourneyData {
  const segments = Array.isArray(raw.segments)
    ? raw.segments.map(transformSessionJourneySegment)
    : [];
  const paymentConfirmed = Array.isArray(raw.paymentConfirmed ?? raw.payment_confirmed)
    ? (raw.paymentConfirmed ?? raw.payment_confirmed).map(transformSessionJourneyPayment)
    : [];
  const paymentConfirmedAt = paymentConfirmed.length
    ? paymentConfirmed.map((p: SessionJourneyPaymentConfirmed) => p.at)
    : Array.isArray(raw.paymentConfirmedAt ?? raw.payment_confirmed_at)
      ? (raw.paymentConfirmedAt ?? raw.payment_confirmed_at).map((t: unknown) => String(t))
      : Array.isArray(raw.paymentSucceededAt ?? raw.payment_succeeded_at)
        ? (raw.paymentSucceededAt ?? raw.payment_succeeded_at).map((t: unknown) => String(t))
        : [];
  return {
    interactionSessionId: String(raw.interactionSessionId ?? raw.interaction_session_id ?? ""),
    startTs: String(raw.startTs ?? raw.start_ts ?? ""),
    endTs: String(raw.endTs ?? raw.end_ts ?? ""),
    spanMs: Number(raw.spanMs ?? raw.span_ms) || 0,
    activeMs: Number(raw.activeMs ?? raw.active_ms) || 0,
    paidSessionId:
      typeof raw.paidSessionId === "string"
        ? raw.paidSessionId
        : typeof raw.paid_session_id === "string"
          ? raw.paid_session_id
          : null,
    paymentConfirmed,
    paymentConfirmedAt,
    paymentSucceededAt: paymentConfirmedAt,
    segments,
    inProgress: Boolean(raw.inProgress ?? raw.in_progress),
    criteria: typeof raw.criteria === "string" ? raw.criteria : undefined,
  };
}

function transformSessions(raw: any): SessionsData {
  const cohortCountsRaw = raw.summary?.cohortCounts ?? raw.summary?.cohort_counts ?? {};
  const paidRaw = raw.summary?.paidVsFreeByCohort ?? raw.summary?.paid_vs_free_by_cohort ?? {};
  const cohortCounts = Object.fromEntries(
    INTERACTION_SESSION_COHORT_ORDER.map((c) => [c, Number(cohortCountsRaw[c]) || 0]),
  ) as Record<InteractionSessionCohort, number>;
  const paidVsFreeByCohort = Object.fromEntries(
    INTERACTION_SESSION_COHORT_ORDER.map((c) => {
      const row = paidRaw[c] ?? {};
      return [
        c,
        {
          paid: Number(row.paid) || 0,
          free: Number(row.free) || 0,
        },
      ];
    }),
  ) as SessionsData["summary"]["paidVsFreeByCohort"];

  const mapTopEvents = (list: unknown[]) =>
    (list ?? []).map((e: any) => ({
      eventName: String(e.eventName ?? e.event_name ?? ""),
      count: Number(e.count) || 0,
      pct: Number(e.pct) || 0,
    }));

  const cohortProfiles = (raw.cohortProfiles ?? raw.cohort_profiles ?? []).map((p: any) => ({
    cohort: String(p.cohort ?? "") as InteractionSessionCohort,
    stintCount: Number(p.stintCount ?? p.stint_count) || 0,
    topEvents: mapTopEvents(p.topEvents ?? p.top_events),
    outcomes: {
      reachedMenuPct: Number(p.outcomes?.reachedMenuPct ?? p.outcomes?.reached_menu_pct) || 0,
      hadGameStartPct: Number(p.outcomes?.hadGameStartPct ?? p.outcomes?.had_game_start_pct) || 0,
      hadPaymentPct: Number(p.outcomes?.hadPaymentPct ?? p.outcomes?.had_payment_pct) || 0,
      hadGameCompletedPct:
        Number(p.outcomes?.hadGameCompletedPct ?? p.outcomes?.had_game_completed_pct) || 0,
      hadGameAbandonedPct:
        Number(p.outcomes?.hadGameAbandonedPct ?? p.outcomes?.had_game_abandoned_pct) || 0,
    },
    shellMs: {
      attract: Number(p.shellMs?.attract ?? p.shell_ms?.attract) || 0,
      mainMenu: Number(p.shellMs?.mainMenu ?? p.shell_ms?.main_menu) || 0,
      game: Number(p.shellMs?.game ?? p.shell_ms?.game) || 0,
      total: Number(p.shellMs?.total ?? p.shell_ms?.total) || 0,
    },
    medians: {
      wallMs: Number(p.medians?.wallMs ?? p.medians?.wall_ms) || 0,
      activeMs: Number(p.medians?.activeMs ?? p.medians?.active_ms) || 0,
      eventCount: Number(p.medians?.eventCount ?? p.medians?.event_count) || 0,
    },
  }));

  const longRaw = raw.longSessions ?? raw.long_sessions ?? {};
  const longRows = (longRaw.rows ?? []).map((r: any) => ({
    interactionSessionId: String(r.interactionSessionId ?? r.interaction_session_id ?? ""),
    spanMs: Number(r.spanMs ?? r.span_ms) || 0,
    activeMs: Number(r.activeMs ?? r.active_ms) || 0,
    eventCount: Number(r.eventCount ?? r.event_count) || 0,
    firstTs: String(r.firstTs ?? r.first_ts ?? ""),
    lastTs: String(r.lastTs ?? r.last_ts ?? ""),
    cohort: String(r.cohort ?? ""),
    deviceId: String(r.deviceId ?? r.device_id ?? ""),
    deviceName: String(r.deviceName ?? r.device_name ?? ""),
    venueId: String(r.venueId ?? r.venue_id ?? ""),
    venueName: String(r.venueName ?? r.venue_name ?? ""),
    venueSource:
      (r.venueSource ?? r.venue_source) === "play_session" ? "play_session" : "device_current",
    hadPaidSessionId: Boolean(r.hadPaidSessionId ?? r.had_paid_session_id),
    hadPaymentSucceeded: Boolean(
      r.hadPaymentConfirmed ??
      r.had_payment_confirmed ??
      r.hadPaymentSucceeded ??
      r.had_payment_succeeded,
    ),
    hadPaymentConfirmed: Boolean(r.hadPaymentConfirmed ?? r.had_payment_confirmed),
    hadGameStart: Boolean(r.hadGameStart ?? r.had_game_start),
    hadGameCompleted: Boolean(r.hadGameCompleted ?? r.had_game_completed),
    hadGameAbandoned: Boolean(r.hadGameAbandoned ?? r.had_game_abandoned),
    pulseCount: Number(r.pulseCount ?? r.pulse_count) || 0,
  }));

  return {
    summary: {
      totalEvents: Number(raw.summary?.totalEvents ?? raw.summary?.total_events) || 0,
      stintCount: Number(raw.summary?.stintCount ?? raw.summary?.stint_count) || 0,
      orphanEventCount:
        Number(raw.summary?.orphanEventCount ?? raw.summary?.orphan_event_count) || 0,
      orphanEventPct: Number(raw.summary?.orphanEventPct ?? raw.summary?.orphan_event_pct) || 0,
      cohortCounts,
      paidVsFreeByCohort,
    },
    orphanProfile: {
      eventCount: Number(raw.orphanProfile?.eventCount ?? raw.orphan_profile?.event_count) || 0,
      topEvents: mapTopEvents(raw.orphanProfile?.topEvents ?? raw.orphan_profile?.top_events),
    },
    cohortProfiles,
    longSessions: {
      minSpanMs: Number(longRaw.minSpanMs ?? longRaw.min_span_ms) || 1_800_000,
      longOnly: Boolean(longRaw.longOnly ?? longRaw.long_only),
      paidOnly: Boolean(longRaw.paidOnly ?? longRaw.paid_only),
      listAllStints: Boolean(
        longRaw.listAllStints ??
        longRaw.list_all_stints ??
        (!(longRaw.longOnly ?? longRaw.long_only) && !(longRaw.paidOnly ?? longRaw.paid_only)),
      ),
      total: Number(longRaw.total) || 0,
      limit: Number(longRaw.limit) || 50,
      offset: Number(longRaw.offset) || 0,
      rows: longRows,
    },
  };
}

function mapChartDevices(raw: any): AcquisitionData["chartDevices"] {
  return (raw.chartDevices ?? raw.chart_devices ?? [])
    .map((d: any) => ({
      id: String(d.id ?? ""),
      name: String(d.name ?? ""),
      venueName: String(d.venueName ?? d.venue_name ?? ""),
    }))
    .filter((d: { id: string }) => d.id);
}

function mapUptimeByDeviceDay(raw: any): AcquisitionData["uptimeByDeviceDay"] {
  return (raw.uptimeByDeviceDay ?? raw.uptime_by_device_day ?? []).map((r: any) => ({
    deviceId: String(r.deviceId ?? r.device_id ?? ""),
    day: String(r.day ?? ""),
    uptimeHours: Number(r.uptimeHours ?? r.uptime_hours) || 0,
    capacityHours: Number(r.capacityHours ?? r.capacity_hours) || 0,
    unmeasuredHours: Number(r.unmeasuredHours ?? r.unmeasured_hours) || 0,
  }));
}

function transformAcquisition(raw: any): AcquisitionData {
  const attractMs = Number(raw.attractTime?.attractMs) || 0;
  const menuMs = Number(raw.attractTime?.menuMs) || 0;
  const gamesMs = Number(raw.attractTime?.gamesMs) || 0;
  const se = raw.attractTime?.fromScreenExitedMs ?? raw.attractTime?.from_screen_exited_ms;
  const pg = raw.attractTime?.fromPulseGapMs ?? raw.attractTime?.from_pulse_gap_ms;
  const hasShellMsBreakdown = se != null && pg != null;
  const attractMsFromScreenExited = hasShellMsBreakdown
    ? Number(se.attractMs ?? se.attract_ms) || 0
    : undefined;
  const mainMenuMsFromScreenExited = hasShellMsBreakdown
    ? Number(se.menuMs ?? se.menu_ms) || 0
    : undefined;
  const gamesMsFromScreenExited = hasShellMsBreakdown
    ? Number(se.gamesMs ?? se.games_ms) || 0
    : undefined;
  const attractMsFromPulseGap = hasShellMsBreakdown
    ? Number(pg.attractMs ?? pg.attract_ms) || 0
    : undefined;
  const mainMenuMsFromPulseGap = hasShellMsBreakdown
    ? Number(pg.menuMs ?? pg.menu_ms) || 0
    : undefined;
  const gamesMsFromPulseGap = hasShellMsBreakdown
    ? Number(pg.gamesMs ?? pg.games_ms) || 0
    : undefined;
  const totalVisible = Math.max(1, attractMs + menuMs + gamesMs);

  const bucketMap: Record<string, number> = {};
  let bucketTotal = 0;
  for (const row of raw.menuDwellHistogram ?? []) {
    const count = Number(row.count) || 0;
    bucketMap[row.bucket] = count;
    bucketTotal += count;
  }
  bucketTotal = Math.max(1, bucketTotal);

  const menu = Number(raw.funnel?.menuOpen) || 0;
  const tutorial = Number(raw.funnel?.tutorialOpen) || 0;
  const paymentOpen = Number(raw.funnel?.paymentOpen) || 0;
  const paid = Number(raw.funnel?.paymentSuccess) || 0;
  const gameStart = Number(raw.funnel?.gameStart) || 0;
  const menuToGame = Number(raw.menuOutcome?.toGame) || 0;
  const menuToAttract = Number(raw.menuOutcome?.toAttract) || 0;
  const paidSessions = Number(raw.paidToGameConversion?.paidSessions) || 0;
  const paidSessionsWithGameStart =
    Number(raw.paidToGameConversion?.paidSessionsWithGameStart) || 0;

  const attractTimeByDay = (raw.attractTimeByDay ?? []).map((r: any) => ({
    day: String(r.day ?? ""),
    attractMs: Number(r.attractMs ?? r.attract_ms) || 0,
    mainMenuMs: Number(r.mainMenuMs ?? r.menuMs ?? r.menu_ms) || 0,
    gamesMs: Number(r.gamesMs ?? r.games_ms) || 0,
  }));

  const interactionStintsByDay = (raw.interactionStintsByDay ?? []).map((r: any) => ({
    day: String(r.day ?? ""),
    stintCount: Number(r.stintCount ?? r.stint_count) || 0,
  }));

  const sessionDurMap: Record<string, number> = {};
  for (const row of raw.interactionSessionDurationHistogram ?? []) {
    sessionDurMap[String(row.bucket ?? "")] = Number(row.count) || 0;
  }

  const rawSanity = raw.coarsePulseSanityCheck ?? raw.coarse_pulse_sanity_check;
  const coarsePulseSanityCheck =
    rawSanity != null && typeof rawSanity === "object"
      ? {
          nominalSlotMs: Number(rawSanity.nominalSlotMs ?? rawSanity.nominal_slot_ms) || 300_000,
          attractPulses: Number(rawSanity.attractPulses ?? rawSanity.attract_pulses) || 0,
          mainMenuPulses: Number(rawSanity.mainMenuPulses ?? rawSanity.main_menu_pulses) || 0,
          gamesPulses: Number(rawSanity.gamesPulses ?? rawSanity.games_pulses) || 0,
          otherPulses: Number(rawSanity.otherPulses ?? rawSanity.other_pulses) || 0,
          attractMsRough: Number(rawSanity.attractMsRough ?? rawSanity.attract_ms_rough) || 0,
          menuMsRough: Number(rawSanity.menuMsRough ?? rawSanity.menu_ms_rough) || 0,
          gamesMsRough: Number(rawSanity.gamesMsRough ?? rawSanity.games_ms_rough) || 0,
        }
      : undefined;

  return {
    eventCount: menu,
    attractMs,
    mainMenuMs: menuMs,
    gamesMs,
    ...(hasShellMsBreakdown
      ? {
          attractMsFromScreenExited,
          mainMenuMsFromScreenExited,
          gamesMsFromScreenExited,
          attractMsFromPulseGap,
          mainMenuMsFromPulseGap,
          gamesMsFromPulseGap,
        }
      : {}),
    attractRatio: attractMs / totalVisible,
    mainMenuRatio: menuMs / totalVisible,
    gamesRatio: gamesMs / totalVisible,
    nonAttractRatio: (menuMs + gamesMs) / totalVisible,
    menuBinsPct: MENU_BUCKET_ORDER.map((b) => (bucketMap[b] ?? 0) / bucketTotal),
    menuOutcome: {
      toGame: menuToGame,
      toAttract: menuToAttract,
    },
    paidToGameConversion: {
      paidSessions,
      paidSessionsWithGameStart,
    },
    funnel: { menu, tutorial, paymentOpen, paid, gameStart },
    funnelDisplay: [menu, tutorial, paymentOpen || paid, paid, gameStart],
    interactionSessionDurationHist: INTERACTION_SESSION_DURATION_BUCKET_ORDER.map(
      (b) => sessionDurMap[b] ?? 0,
    ),
    attractTimeByDay,
    interactionStintsByDay,
    interactionStintsByHour: Array.from({ length: 24 }, (_, hour) => {
      const row = (raw.interactionStintsByHour ?? raw.interaction_stints_by_hour ?? []).find(
        (r: any) => Number(r.hour) === hour,
      );
      return {
        hour,
        stintCount: row ? Number(row.stintCount ?? row.stint_count) || 0 : 0,
      };
    }),
    interactionStintsPerUptimeHourByDay: (
      raw.interactionStintsPerUptimeHourByDay ??
      raw.interaction_stints_per_uptime_hour_by_day ??
      []
    ).map((r: any) => {
      const uptimeHours = Number(r.uptimeHours ?? r.uptime_hours) || 0;
      const stintCount = Number(r.stintCount ?? r.stint_count) || 0;
      const rawRate = r.stintsPerUptimeHour ?? r.stints_per_uptime_hour;
      const stintsPerUptimeHour =
        rawRate == null || rawRate === ""
          ? uptimeHours > 0
            ? stintCount / uptimeHours
            : null
          : Number(rawRate);
      return {
        day: String(r.day ?? ""),
        stintCount,
        uptimeHours,
        stintsPerUptimeHour:
          stintsPerUptimeHour != null && Number.isFinite(stintsPerUptimeHour)
            ? stintsPerUptimeHour
            : null,
        capacityHours: Number(r.capacityHours ?? r.capacity_hours) || 0,
        unmeasuredHours: Number(r.unmeasuredHours ?? r.unmeasured_hours) || 0,
      };
    }),
    uniqueTableCount: Number(raw.uniqueTableCount ?? raw.unique_table_count) || 0,
    chartDevices: mapChartDevices(raw),
    uptimeByDeviceDay: mapUptimeByDeviceDay(raw),
    stintsByDeviceDay: (raw.stintsByDeviceDay ?? raw.stints_by_device_day ?? []).map((r: any) => ({
      deviceId: String(r.deviceId ?? r.device_id ?? ""),
      day: String(r.day ?? ""),
      stintCount: Number(r.stintCount ?? r.stint_count) || 0,
    })),
    paymentFunnelByVenue: (raw.paymentFunnelByVenue ?? raw.payment_funnel_by_venue ?? []).map(
      (r: any) => ({
        venueId: String(r.venueId ?? r.venue_id ?? ""),
        venueName: String(r.venueName ?? r.venue_name ?? r.name ?? ""),
        interactionStints: Number(r.interactionStints ?? r.interaction_stints) || 0,
        tableQrCode: Number(r.tableQrCode ?? r.table_qr_code) || 0,
        mobilePageview: Number(r.mobilePageview ?? r.mobile_pageview) || 0,
        mobilePageviewEvents:
          Number(r.mobilePageviewEvents ?? r.mobile_pageview_events ?? r.mobilePageview ?? r.mobile_pageview) ||
          0,
        paymentAttempted: Number(r.paymentAttempted ?? r.payment_attempted) || 0,
        paymentAttemptedEvents:
          Number(
            r.paymentAttemptedEvents ??
              r.payment_attempted_events ??
              r.paymentAttempted ??
              r.payment_attempted,
          ) || 0,
        paymentSucceeded:
          Number(
            r.paymentConfirmed ?? r.payment_confirmed ?? r.paymentSucceeded ?? r.payment_succeeded,
          ) || 0,
        paymentConfirmed:
          Number(
            r.paymentConfirmed ?? r.payment_confirmed ?? r.paymentSucceeded ?? r.payment_succeeded,
          ) || 0,
        grossRevPence: Number(r.grossRevPence ?? r.gross_rev_pence) || 0,
      }),
    ),
    ...(coarsePulseSanityCheck ? { coarsePulseSanityCheck } : {}),
  };
}

function transformEngagement(raw: any): EngagementData {
  const pickStr = (r: Record<string, unknown>, snake: string, camel: string): string => {
    const a = r[snake];
    const b = r[camel];
    if (a != null && String(a).trim() !== "") return String(a);
    if (b != null && String(b).trim() !== "") return String(b);
    return "";
  };
  const pickNum = (r: Record<string, unknown>, snake: string, camel: string): number => {
    const raw = r[snake] ?? r[camel];
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const minutesPerGame = (raw.minutesPerGame ?? []).map((r: any) => ({
    game_url: pickStr(r, "game_url", "gameUrl"),
    total_minutes: pickNum(r, "total_minutes", "totalMinutes"),
  }));
  const totalPlayMinutes = minutesPerGame.reduce((s: number, r: any) => s + r.total_minutes, 0);
  const totalMinutes = Math.max(1, totalPlayMinutes);

  const minutesPerStart = (raw.minutesPerStart ?? []).map((r: any) => ({
    game: pickStr(r, "game_url", "gameUrl"),
    value: pickNum(r, "minutes_per_start", "minutesPerStart"),
  }));

  const minutesPerClick = (raw.minutesPerClick ?? []).map((r: any) => {
    const hasNewRatio =
      Object.hasOwn(r, "minutes_per_start") || Object.hasOwn(r, "minutesPerStart");
    const value = hasNewRatio
      ? pickNum(r, "minutes_per_start", "minutesPerStart")
      : pickNum(r, "minutes_per_click", "minutesPerClick");
    return {
      game: pickStr(r, "game_url", "gameUrl"),
      value,
      iconClicks: pickNum(r, "icon_clicks", "iconClicks"),
      tutorialPlay: pickNum(r, "tutorial_play", "tutorialPlay"),
      tutorialDismiss: pickNum(r, "tutorial_dismiss", "tutorialDismiss"),
      tutorialPlayPct:
        r.tutorial_play_pct != null && Number.isFinite(Number(r.tutorial_play_pct))
          ? Number(r.tutorial_play_pct)
          : r.tutorialPlayPct != null && Number.isFinite(Number(r.tutorialPlayPct))
            ? Number(r.tutorialPlayPct)
            : null,
      surveyCount: pickNum(r, "survey_count", "surveyCount"),
      avgRating:
        r.avg_rating != null && Number.isFinite(Number(r.avg_rating))
          ? Number(r.avg_rating)
          : r.avgRating != null && Number.isFinite(Number(r.avgRating))
            ? Number(r.avgRating)
            : null,
    };
  });

  const gamesCompletedByGame = (raw.gamesCompletedByGame ?? []).map((r: any) => {
    const count = pickNum(r, "count", "count");
    let firstCount = pickNum(r, "first_count", "firstCount");
    let repeatCount = pickNum(r, "repeat_count", "repeatCount");
    const hasSplitKeys =
      Object.hasOwn(r, "first_count") ||
      Object.hasOwn(r, "repeat_count") ||
      Object.hasOwn(r, "firstCount") ||
      Object.hasOwn(r, "repeatCount");
    if (count > 0 && !hasSplitKeys) {
      firstCount = count;
      repeatCount = 0;
    } else if (count > 0 && firstCount + repeatCount === 0) {
      firstCount = count;
      repeatCount = 0;
    }
    return {
      game: pickStr(r, "game_url", "gameUrl"),
      count,
      firstCount,
      repeatCount,
      abandonedCount: pickNum(r, "abandoned_count", "abandonedCount"),
    };
  });

  const totalGamesCompleted = gamesCompletedByGame.reduce(
    (s: number, r: { count: number }) => s + r.count,
    0,
  );
  const totalGamesAbandoned = gamesCompletedByGame.reduce(
    (s: number, r: { abandonedCount: number }) => s + r.abandonedCount,
    0,
  );

  const avgLoadRows = raw.avgLoadTimeByGame ?? raw.avgLoadTime ?? [];
  const avgLoadMsByGame = avgLoadRows.map((r: any) => {
    const ms = pickNum(r, "avg_load_ms", "avgLoadMs");
    return {
      game: pickStr(r, "game_url", "gameUrl"),
      value: ms,
      valueLabel: `${(ms / 1000).toFixed(2)}s`,
    };
  });

  const sessionHist: number[] = [];
  const histBuckets = ["<10s", "10-30s", "30-2m", "2-5m", "5m+"];
  const histMap: Record<string, number> = {};
  const sessionDurationRows = raw.sessionDurationHistogram ?? raw.playTimeHistogram ?? [];
  for (const row of sessionDurationRows) {
    histMap[row.bucket] = Number(row.count) || 0;
  }
  for (const b of histBuckets) {
    sessionHist.push(histMap[b] ?? 0);
  }

  const topGame = minutesPerGame[0];
  const bestMps =
    minutesPerStart.length > 0
      ? minutesPerStart.reduce(
          (best: { game: string; value: number }, r: { game: string; value: number }) =>
            r.value > best.value ? r : best,
        )
      : null;

  const allLoadMs =
    avgLoadMsByGame.length > 0
      ? avgLoadMsByGame.reduce((s: number, r: any) => s + r.value, 0) / avgLoadMsByGame.length
      : 0;

  const surveyRatings = Array.from({ length: 5 }, (_, i) => {
    const row = (raw.surveyRatings ?? []).find((r: any) => Number(r.rating) === i + 1);
    return row ? Number(row.count) || 0 : 0;
  });
  const surveyVolume = surveyRatings.reduce((a, b) => a + b, 0);
  const surveyComments = (raw.recentComments ?? []).map((r: any) => r.comment ?? "");

  return {
    eventCount: minutesPerGame.length,
    totalPlayMinutes,
    totalGamesCompleted,
    byGameMinutesPct: minutesPerGame.map((r: any) => {
      const pct = r.total_minutes / totalMinutes;
      return {
        label: r.game_url,
        value: pct,
        minutes: r.total_minutes,
        valueLabel: `${Math.round(pct * 100)}% (${Number(r.total_minutes).toFixed(1)}m)`,
      };
    }),
    gamesCompletedByGame,
    totalGamesAbandoned,
    minutesPerStart,
    minutesPerClick,
    avgLoadMsByGame,
    sessionHist,
    topGameLabel: topGame?.game_url ?? "n/a",
    topGameMinutes: topGame?.total_minutes ?? 0,
    bestMinutesPerStart: bestMps,
    avgLoadTimeMs: allLoadMs,
    surveyVolume,
    surveyRatings,
    surveyComments,
  };
}

function transformRetention(raw: any): RetentionData {
  const pf = raw.paymentFunnel ?? raw.payment_funnel ?? {};
  const interactionStints = Number(pf.interactionStints ?? pf.interaction_stints) || 0;
  const tableQrCode = Number(pf.tableQrCode ?? pf.table_qr_code) || 0;
  const mobilePageview = Number(pf.mobilePageview ?? pf.mobile_pageview) || 0;
  const mobilePageviewEvents =
    Number(pf.mobilePageviewEvents ?? pf.mobile_pageview_events ?? mobilePageview) || 0;
  const paymentAttempted = Number(pf.paymentAttempted ?? pf.payment_attempted) || 0;
  const paymentAttemptedEvents =
    Number(pf.paymentAttemptedEvents ?? pf.payment_attempted_events ?? paymentAttempted) || 0;
  const paymentConfirmed =
    Number(
      pf.paymentConfirmed ?? pf.payment_confirmed ?? pf.paymentSucceeded ?? pf.payment_succeeded,
    ) || 0;
  const tablePaymentSucceeded = Number(pf.tablePaymentSucceeded ?? pf.table_payment_succeeded) || 0;
  const grossRevPence = Number(pf.grossRevPence ?? pf.gross_rev_pence) || 0;
  const unregisteredPayments = Number(pf.unregisteredPayments ?? pf.unregistered_payments) || 0;
  const unregisteredRevPence = Number(pf.unregisteredRevPence ?? pf.unregistered_rev_pence) || 0;
  const registeredFromApi = pf.registeredPayments ?? pf.registered_payments;
  const registeredPayments =
    registeredFromApi != null
      ? Number(registeredFromApi) || 0
      : Math.max(0, paymentConfirmed - unregisteredPayments);
  const paidSessionTotal = registeredPayments + unregisteredPayments;

  const paymentAmounts = (raw.paymentAmounts ?? raw.payment_amounts ?? []).map((row: any) => ({
    amountPence: Number(row.amountPence ?? row.amount_pence) || 0,
    count: Number(row.count) || 0,
  }));

  const outcomesRaw = raw.paymentOutcomes ?? raw.payment_outcomes ?? {};
  const paymentOutcomes = {
    slices: (outcomesRaw.slices ?? []).map((s: any) => ({
      key: String(s.key ?? ""),
      label: String(s.label ?? ""),
      count: Number(s.count) || 0,
    })),
    paymentAttempted: Number(outcomesRaw.paymentAttempted ?? outcomesRaw.payment_attempted) || 0,
    distinctCheckoutOutcomes:
      Number(outcomesRaw.distinctCheckoutOutcomes ?? outcomesRaw.distinct_checkout_outcomes) || 0,
  };

  return {
    eventCount: paymentConfirmed,
    repeatSessions: 0,
    repeatMobiles: Number(raw.payers?.repeat) || 0,
    newPayers: Number(raw.payers?.new) || 0,
    repeatPayers: Number(raw.payers?.repeat) || 0,
    grossRevPence,
    registeredPayments,
    unregisteredPayments,
    unregisteredRevPence,
    tablePaymentSucceeded,
    paymentAmounts,
    paymentOutcomes,
    funnelDisplay: [
      interactionStints,
      tableQrCode,
      mobilePageview,
      paymentAttempted,
      // Play-session grain (explorer total); fall back to checkout confirms if split is empty.
      paidSessionTotal > 0 ? paidSessionTotal : paymentConfirmed,
    ],
    mobilePageviewEvents,
    paymentAttemptedEvents,
    revenueByDay: (raw.revenueByDay ?? raw.revenue_by_day ?? []).map((r: any) => {
      const revenuePence = Number(r.revenuePence ?? r.revenue_pence) || 0;
      const uptimeHours = Number(r.uptimeHours ?? r.uptime_hours) || 0;
      const rawRate = r.revenuePerUptimeHour ?? r.revenue_per_uptime_hour;
      const revenuePerUptimeHour =
        rawRate == null || rawRate === ""
          ? uptimeHours > 0
            ? revenuePence / 100 / uptimeHours
            : null
          : Number(rawRate);
      return {
        day: String(r.day ?? ""),
        revenuePence,
        uptimeHours,
        revenuePerUptimeHour:
          revenuePerUptimeHour != null && Number.isFinite(revenuePerUptimeHour)
            ? revenuePerUptimeHour
            : null,
        capacityHours: Number(r.capacityHours ?? r.capacity_hours) || 0,
        unmeasuredHours: Number(r.unmeasuredHours ?? r.unmeasured_hours) || 0,
      };
    }),
    revenueByHour: Array.from({ length: 24 }, (_, hour) => {
      const row = (raw.revenueByHour ?? raw.revenue_by_hour ?? []).find(
        (r: { hour?: unknown }) => Number(r.hour) === hour,
      );
      return {
        hour,
        revenuePence:
          Number(
            row?.revenuePence ?? row?.revenue_pence ?? row?.amountPence ?? row?.amount_pence,
          ) || 0,
        paymentCount: Number(row?.paymentCount ?? row?.payment_count) || 0,
      };
    }),
    revenueTableCount: Number(raw.revenueTableCount ?? raw.revenue_table_count) || 0,
    chartDevices: mapChartDevices(raw),
    uptimeByDeviceDay: mapUptimeByDeviceDay(raw),
    revenueByDeviceDay: (raw.revenueByDeviceDay ?? raw.revenue_by_device_day ?? []).map(
      (r: any) => ({
        deviceId: String(r.deviceId ?? r.device_id ?? ""),
        day: String(r.day ?? ""),
        revenuePence: Number(r.revenuePence ?? r.revenue_pence ?? r.amountPence ?? r.amount_pence) || 0,
      }),
    ),
    stintsByDeviceDay: (raw.stintsByDeviceDay ?? raw.stints_by_device_day ?? []).map((r: any) => ({
      deviceId: String(r.deviceId ?? r.device_id ?? ""),
      day: String(r.day ?? ""),
      stintCount: Number(r.stintCount ?? r.stint_count) || 0,
    })),
    paymentConfirmedByDeviceDay: (
      raw.paymentConfirmedByDeviceDay ?? raw.payment_confirmed_by_device_day ?? []
    ).map((r: any) => ({
      deviceId: String(r.deviceId ?? r.device_id ?? ""),
      day: String(r.day ?? ""),
      paymentConfirmedCount:
        Number(r.paymentConfirmedCount ?? r.payment_confirmed_count) || 0,
    })),
    stintsToPaymentConfirmedByDay: (
      raw.stintsToPaymentConfirmedByDay ?? raw.stints_to_payment_confirmed_by_day ?? []
    ).map((r: any) => {
      const stintCount = Number(r.stintCount ?? r.stint_count) || 0;
      const paymentConfirmedCount =
        Number(r.paymentConfirmedCount ?? r.payment_confirmed_count) || 0;
      const rawPct = r.pct ?? r.payment_confirmed_pct;
      const pct =
        rawPct == null || rawPct === ""
          ? stintCount > 0
            ? paymentConfirmedCount / stintCount
            : null
          : Number(rawPct);
      return {
        day: String(r.day ?? ""),
        stintCount,
        paymentConfirmedCount,
        pct: pct != null && Number.isFinite(pct) ? pct : null,
        capacityHours: Number(r.capacityHours ?? r.capacity_hours) || 0,
      };
    }),
  };
}

export const api = {
  presence: (filters: FilterState) => fetchJson<PresenceData>("/api/analytics/presence", { ...filters, gameUrl: "all" }),
  me: () => fetchJson<MeResponse>("/api/analytics/me"),
  /** Unauthenticated, so the shell can offer a sign-in link before it knows who the visitor is. */
  signInUrl: async (): Promise<string> => {
    const { baseUrl } = getApiSourceInfo();
    const res = await fetch(new URL("/api/analytics/sign-in-url", baseUrl).toString(), {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    return ((await res.json()) as { signInUrl: string }).signInUrl;
  },
  /** Fleet-aware: a staff user narrowing to one fleet should see only that fleet's venues. */
  filters: (fleetId?: string) => {
    const params = new URLSearchParams();
    if (fleetId && fleetId !== "all") params.set("fleet_id", fleetId);
    const query = params.toString();
    return fetchJson<FilterOptions>(
      query ? `/api/analytics/filters?${query}` : "/api/analytics/filters",
    );
  },
  health: async (filters: FilterState): Promise<HealthData> => {
    const raw = await fetchJson<any>("/api/analytics/health", filters);
    return transformHealth(raw);
  },
  healthWifi: (filters: FilterState) => fetchJson<WifiOverview>("/api/analytics/health/wifi", filters),
  healthWifiSignal: async (
    filters: FilterState,
    deviceId: string,
  ): Promise<WifiSignalSeries> => {
    const { baseUrl } = getApiSourceInfo();
    const url = new URL("/api/analytics/health/wifi-signal", baseUrl);
    const params = filtersToParams(filters);
    params.set("device_id", deviceId);
    params.forEach((value, key) => url.searchParams.set(key, value));
    const res = await fetchOrExplain(url);
    await throwForAuth(res);
    if (!res.ok) {
      throw new Error(`API error: ${res.status} ${await readErrorDetail(res)}`);
    }
    const raw = (await res.json()) as WifiSignalSeries;
    const grain =
      raw.grain === "hour" ? "hour" : raw.grain === "day" ? "day" : "minute";
    return {
      available: Boolean(raw.available),
      deviceId: typeof raw.deviceId === "string" ? raw.deviceId : deviceId,
      deviceName: typeof raw.deviceName === "string" ? raw.deviceName : "",
      venueName: typeof raw.venueName === "string" ? raw.venueName : "",
      grain,
      points: (raw.points ?? []).map((row) => ({
        at: String(row.at ?? ""),
        rssiDbm: row.rssiDbm == null ? null : Number(row.rssiDbm),
        txMbps: row.txMbps == null ? null : Number(row.txMbps),
      })),
    };
  },
  healthTelemetryMinutes: async (
    filters: FilterState,
    query: HealthTelemetryMinutesQuery,
  ): Promise<HealthTelemetryMinutesResult> => {
    const { baseUrl } = getApiSourceInfo();
    const url = new URL("/api/analytics/health/telemetry-minutes", baseUrl);
    const params = filtersToParams(filters);
    params.set("kind", query.kind);
    if (query.deviceId) params.set("device_id", query.deviceId);
    if (query.tempCelsiusMin != null) params.set("temp_celsius_min", String(query.tempCelsiusMin));
    if (query.tempCelsiusMaxExclusive != null) {
      params.set("temp_celsius_max_exclusive", String(query.tempCelsiusMaxExclusive));
    }
    params.set("limit", String(query.limit ?? 100));
    params.set("offset", String(query.offset ?? 0));
    params.forEach((value, key) => url.searchParams.set(key, value));
    const res = await fetchOrExplain(url);
    await throwForAuth(res);
    if (!res.ok) {
      throw new Error(`API error: ${res.status} ${await readErrorDetail(res)}`);
    }
    const raw = (await res.json()) as HealthTelemetryMinutesResult;
    return {
      available: Boolean(raw.available),
      kind:
        raw.kind === "wifi_bad" ? "wifi_bad" : raw.kind === "wifi_good" ? "wifi_good" : "heat",
      total: Number(raw.total) || 0,
      limit: Number(raw.limit) || 100,
      offset: Number(raw.offset) || 0,
      cutoverIso: typeof raw.cutoverIso === "string" ? raw.cutoverIso : null,
      speedTests: (raw.speedTests ?? []).map((row) => ({
        testedAt: String(row.testedAt ?? ""),
        downloadMbps: row.downloadMbps == null ? null : Number(row.downloadMbps),
        uploadMbps: row.uploadMbps == null ? null : Number(row.uploadMbps),
      })),
      rows: (raw.rows ?? []).map((row) => ({
        deviceId: String(row.deviceId ?? ""),
        deviceName: String(row.deviceName ?? "").trim(),
        venueName: String(row.venueName ?? "").trim(),
        minuteStart: String(row.minuteStart ?? ""),
        socTempCelsius: row.socTempCelsius == null ? null : Number(row.socTempCelsius),
        connected: row.connected == null ? null : Boolean(row.connected),
        rssiDbm: row.rssiDbm == null ? null : Number(row.rssiDbm),
        txMbps: row.txMbps == null ? null : Number(row.txMbps),
        rxMbps: row.rxMbps == null ? null : Number(row.rxMbps),
      })),
    };
  },
  /** Pretty-printed JSON file: all client_error rows in scope, same filters as Health. */
  downloadHealthClientErrorEventsJson: async (filters: FilterState): Promise<void> => {
    const text = await fetchExportText("/api/analytics/health/client-error-events-export", filters);
    triggerBrowserDownload(
      text,
      `client-error-events-${filters.from}_to_${filters.to}.json`,
      "application/json;charset=utf-8",
    );
  },
  /**
   * CSV: table + venue × reporting day matrix of effective payment_confirmed amounts (£).
   * Same filters as Retention Gross Rev. (date + venue + device; no Game).
   */
  downloadVenuePaymentsByDayCsv: async (filters: FilterState): Promise<void> => {
    const text = await fetchExportText(
      "/api/analytics/retention/venue-payments-by-day-export",
      filters,
      undefined,
      { expectContentType: "csv" },
    );
    triggerBrowserDownload(
      text,
      `table-venue-payments-by-day-${filters.from}_to_${filters.to}.csv`,
      "text/csv;charset=utf-8",
    );
  },
  /** Pretty-printed JSON: first N matching Raw Events rows (default 100, max 1000), same filters as explorer. */
  downloadRawEventsExportJson: async (
    filters: FilterState,
    opts: {
      exportLimit: number;
      eventName: string;
      propertiesContains: string;
      propertiesContains2: string;
      clientErrorBucket?: "warning" | "error";
      interactionSessionId?: string;
      interactionSessionCohort?: InteractionSessionCohort;
      showTemperatureEvents?: boolean;
      showUptimePulse?: boolean;
      hideAttractBasics?: boolean;
      dedupePopupTapMissed?: boolean;
      tempCelsiusMin?: number;
      tempCelsiusMaxExclusive?: number;
      sortBy?: EventsSortColumn;
      sortDir?: EventsSortDir;
    },
  ): Promise<void> => {
    const lim = Math.min(1000, Math.max(1, Math.floor(opts.exportLimit)));
    const extra: Record<string, string> = { limit: String(lim) };
    if (opts.eventName && opts.eventName !== "all") extra.event_name = opts.eventName;
    const propQ = opts.propertiesContains.trim();
    if (propQ) extra.properties_contains = propQ.slice(0, 500);
    const prop2 = opts.propertiesContains2.trim();
    if (prop2) extra.properties_contains_2 = prop2.slice(0, 500);
    if (
      opts.eventName === "client_error" &&
      (opts.clientErrorBucket === "warning" || opts.clientErrorBucket === "error")
    ) {
      extra.client_error_bucket = opts.clientErrorBucket;
    }
    const isid = opts.interactionSessionId?.trim();
    if (isid) extra.interaction_session_id = isid.slice(0, 128);
    if (opts.interactionSessionCohort) {
      extra.interaction_session_cohort = opts.interactionSessionCohort;
    }
    if (opts.showTemperatureEvents) {
      extra.show_temperature_events = "1";
    } else if (opts.showTemperatureEvents === false) {
      extra.show_temperature_events = "0";
    }
    if (opts.showUptimePulse) {
      extra.show_uptime_pulse = "1";
    } else if (opts.showUptimePulse === false) {
      extra.show_uptime_pulse = "0";
    }
    if (opts.hideAttractBasics) extra.hide_attract_basics = "1";
    if (opts.dedupePopupTapMissed === false) extra.dedupe_popup_tap_missed = "0";
    if (opts.tempCelsiusMin != null) extra.temp_celsius_min = String(opts.tempCelsiusMin);
    if (opts.tempCelsiusMaxExclusive != null) {
      extra.temp_celsius_max_exclusive = String(opts.tempCelsiusMaxExclusive);
    }
    if (opts.sortBy && opts.sortBy !== "client_timestamp") extra.sort_by = opts.sortBy;
    if (opts.sortDir && opts.sortDir !== "desc") extra.sort_dir = opts.sortDir;
    const text = await fetchExportText("/api/analytics/events/export", filters, extra);
    triggerBrowserDownload(
      text,
      `raw-events-${filters.from}_to_${filters.to}.json`,
      "application/json;charset=utf-8",
    );
  },
  acquisition: async (filters: FilterState): Promise<AcquisitionData> => {
    const raw = await fetchJson<any>("/api/analytics/acquisition", filters);
    return transformAcquisition(raw);
  },
  engagement: async (filters: FilterState): Promise<EngagementData> => {
    const raw = await fetchJson<any>("/api/analytics/engagement", filters);
    return transformEngagement(raw);
  },
  retention: async (filters: FilterState): Promise<RetentionData> => {
    const raw = await fetchJson<any>("/api/analytics/retention", filters);
    return transformRetention(raw);
  },
  retentionPaidSessions: async (
    filters: FilterState,
    options?: { limit?: number; offset?: number },
  ): Promise<PaidSessionExplorerData> => {
    const params = filtersToParams(filters);
    params.set("limit", String(options?.limit ?? 25));
    params.set("offset", String(options?.offset ?? 0));
    const raw = await fetchJson<any>(`/api/analytics/retention/paid-sessions?${params.toString()}`);
    return {
      total: Number(raw.total) || 0,
      limit: Number(raw.limit) || 25,
      offset: Number(raw.offset) || 0,
      rows: (raw.rows ?? []).map((r: any) => ({
        paidSessionId: String(r.paidSessionId ?? r.paid_session_id ?? ""),
        firstTs: String(r.firstTs ?? r.first_ts ?? ""),
        lastTs: String(r.lastTs ?? r.last_ts ?? ""),
        activeMs: Number(r.activeMs ?? r.active_ms) || 0,
        deviceId: String(r.deviceId ?? r.device_id ?? ""),
        deviceName: String(r.deviceName ?? r.device_name ?? ""),
        venueId: String(r.venueId ?? r.venue_id ?? ""),
        venueName: String(r.venueName ?? r.venue_name ?? ""),
        venueSource:
          String(r.venueSource ?? r.venue_source ?? "") === "play_session"
            ? "play_session"
            : "device_current",
        interactionSessionId: String(
          r.interactionSessionId ?? r.interaction_session_id ?? "",
        ).trim(),
        amountPence: Number(r.amountPence ?? r.amount_pence) || 0,
        registered: r.registered !== false && r.registered !== "false",
      })),
    };
  },
  sessions: async (
    filters: FilterState,
    options?: {
      limit?: number;
      offset?: number;
      minSpanMs?: number;
      longOnly?: boolean;
      paidOnly?: boolean;
    },
  ): Promise<SessionsData> => {
    const params = filtersToParams(filters);
    params.set("limit", String(options?.limit ?? 50));
    params.set("offset", String(options?.offset ?? 0));
    if (options?.minSpanMs != null) {
      params.set("min_span_ms", String(options.minSpanMs));
    }
    if (options?.longOnly) {
      params.set("long_only", "1");
    }
    if (options?.paidOnly) {
      params.set("paid_only", "1");
    }
    const raw = await fetchJson<any>(`/api/analytics/sessions?${params.toString()}`);
    return transformSessions(raw);
  },
  sessionJourney: async (
    filters: FilterState,
    interactionSessionId: string,
  ): Promise<SessionJourneyData> => {
    const params = filtersToParams(filters);
    params.set("interaction_session_id", interactionSessionId.trim().slice(0, 128));
    const raw = await fetchJson<any>(`/api/analytics/sessions/journey?${params.toString()}`);
    return transformSessionJourney(raw);
  },
  events: async (
    filters: FilterState,
    options?: {
      limit?: number;
      offset?: number;
      eventName?: string;
      propertiesContains?: string;
      propertiesContains2?: string;
      clientErrorBucket?: "warning" | "error";
      interactionSessionId?: string;
      interactionSessionCohort?: InteractionSessionCohort;
      showTemperatureEvents?: boolean;
      showUptimePulse?: boolean;
      hideAttractBasics?: boolean;
      dedupePopupTapMissed?: boolean;
      tempCelsiusMin?: number;
      tempCelsiusMaxExclusive?: number;
      sortBy?: EventsSortColumn;
      sortDir?: EventsSortDir;
    },
  ): Promise<EventsData> => {
    const params = filtersToParams(filters);
    params.set("limit", String(options?.limit ?? 50));
    params.set("offset", String(options?.offset ?? 0));
    if (options?.eventName && options.eventName !== "all") {
      params.set("event_name", options.eventName);
    }
    if (
      options?.eventName === "client_error" &&
      (options.clientErrorBucket === "warning" || options.clientErrorBucket === "error")
    ) {
      params.set("client_error_bucket", options.clientErrorBucket);
    }
    const propQ = options?.propertiesContains?.trim();
    if (propQ) params.set("properties_contains", propQ.slice(0, 500));
    const prop2 = options?.propertiesContains2?.trim();
    if (prop2) params.set("properties_contains_2", prop2.slice(0, 500));
    const isid = options?.interactionSessionId?.trim();
    if (isid) params.set("interaction_session_id", isid.slice(0, 128));
    if (options?.interactionSessionCohort) {
      params.set("interaction_session_cohort", options.interactionSessionCohort);
    }
    if (options?.showTemperatureEvents) {
      params.set("show_temperature_events", "1");
    } else {
      params.set("show_temperature_events", "0");
    }
    if (options?.showUptimePulse) {
      params.set("show_uptime_pulse", "1");
    } else {
      params.set("show_uptime_pulse", "0");
    }
    if (options?.hideAttractBasics) params.set("hide_attract_basics", "1");
    if (options?.dedupePopupTapMissed === false) params.set("dedupe_popup_tap_missed", "0");
    if (options?.tempCelsiusMin != null) {
      params.set("temp_celsius_min", String(options.tempCelsiusMin));
    }
    if (options?.tempCelsiusMaxExclusive != null) {
      params.set("temp_celsius_max_exclusive", String(options.tempCelsiusMaxExclusive));
    }
    const sortBy = options?.sortBy ?? "client_timestamp";
    const sortDir = options?.sortDir ?? "desc";
    if (sortBy !== "client_timestamp") params.set("sort_by", sortBy);
    if (sortDir !== "desc") params.set("sort_dir", sortDir);
    const raw = await fetchJson<any>(`/api/analytics/events?${params.toString()}`);
    const rawSortBy = raw.sortBy ?? raw.sort_by;
    const parsedSortBy: EventsSortColumn =
      rawSortBy === "event_name" ||
      rawSortBy === "device_id" ||
      rawSortBy === "properties" ||
      rawSortBy === "client_timestamp"
        ? rawSortBy
        : "client_timestamp";
    const rawSortDir = raw.sortDir ?? raw.sort_dir;
    const parsedSortDir: EventsSortDir = rawSortDir === "asc" ? "asc" : "desc";
    return {
      total: Number(raw.total) || 0,
      limit: Number.isFinite(Number(raw.limit)) ? Number(raw.limit) : 50,
      offset: Number.isFinite(Number(raw.offset)) ? Number(raw.offset) : 0,
      hasMore: Boolean(raw.hasMore ?? raw.has_more),
      sortBy: parsedSortBy,
      sortDir: parsedSortDir,
      eventNames: (raw.eventNames ?? raw.event_names ?? []).map((r: any) => ({
        name: String(r.name ?? ""),
        count: Number(r.count) || 0,
        clientErrorWarningCount: Number(r.client_error_warning_count) || 0,
      })),
      rows: (raw.rows ?? []) as EventsData["rows"],
    };
  },
};
